import React, {useEffect, useState} from "react";
import {useVsiAppContext} from "../../../../stores/VsiApp";
import {useSearchParams} from "react-router-dom";
import {useDataWithRoute, useTitleFromComponent} from "../../../../utils/hooks";
import {VsiBubble, VsiH4,} from "../../../../styled/App.styled";
import Grid from "../../../../components/Grid";
import {clusterModeling, clusterHostUsageHistory} from "../../../../utils/gridColumnDefs/virtual/clusterModeling";
import Stockchart from "../../../../components/Stockchart";
import VsiLabeledSelect from "../../../../components/VsiLabeledSelect";
import {formatDualAxisStockChartConfig, formatStockChartConfig} from "../../../../utils/Helpers";
import {Card} from "reactstrap";

const metricTypes = [
    {
        value: 'vms', label: 'VMs',
        chartDefs: [
            {
                name: '# of VMs',
                field: 'num_vms',
            },
            {
                name: '# of Active VMs',
                field: 'num_vms',
            },
            {
                name: 'VM Build Capacity',
                field: 'vm_build_capacity',
            },
            {
                name: 'VM Total Capacity',
                field: 'vm_capacity',
            },
        ]
    },
    {
        value: 'none', label: 'None',
        chartDefs: []
    },
    {
        value: 'utilization', label: 'Utilization %',
        chartDefs: [
            {
                name: '% CPU',
                field: 'pct_cpu_util',
            },
            {
                name: '% Memory',
                field: 'pct_memory_util',
            },
            {
                name: '% Disk',
                field: 'pct_disk_util',
            },
        ]
    },
    {
        value: 'cpu', label: 'CPUs',
        chartDefs: [
            {
                name: 'Total CPUs',
                field: 'total_cpus',
            },
            {
                name: 'Total Virtual CPUs',
                field: 'total_virtual_cpus',
            },
            {
                name: 'Total Virtual CPUs Used',
                field: 'total_virtual_cpus_used',
            },
            {
                name: 'Avg. VM CPU Used',
                field: 'average_vm_cpu_size',
            },
        ]
    },
    {
        value: 'memory', label: 'Memory (GiB)',
        chartDefs: [
            {
                name: 'Total Memory',
                field: 'total_memory',
            },
            {
                name: 'Memory Used',
                field: 'memory_used',
            },
            {
                name: 'Avg. VM Memory Size',
                field: 'average_vm_memory_size',
            },
        ]
    },
    {
        value: 'disk', label: 'Disk (GiB)',
        chartDefs: [
            {
                name: 'Total Capacity',
                field: 'allocated',
            },
            {
                name: 'Disk Used',
                field: 'used',
            },
            {
                name: 'Avg. VM Disk Size',
                field: 'average_vm_disk_size',
            },
        ]
    },
];

const usageMetricTypes = [
    {
        value: 'utilization', label: 'Utilization %',
        cpu: '%',
        disk: '%',
        memory: '%',
    },
    {
        value: 'used', label: 'Used',
        cpu: 'GHz',
        disk: 'GiB',
        memory: 'GiB',
    },
    {
        value: 'total', label: 'Total',
        cpu: 'MHz',
        disk: 'GiB',
        memory: 'GiB',
    },
];

const usageMeasurementTypes = [
    {
        value: 'cpu', label: 'CPUs',
        utilization: 'avg_cpu_util',
        used: 'avg_ghz_per_cpu',
        total: 'total_mhz',
    },
    {
        value: 'memory', label: 'Memory',
        utilization: 'avg_memory_util',
        total: 'total_memory',
    },
    {
        value: 'disk', label: 'Disk',
        utilization: 'avg_disk_util',
        used: 'total_disk_used',
        total: 'total_disk_allocated',
    },
];

const chartTypes = [
    {
        value: 'history',
        label: 'Cluster History',
        procName: 'virtual_cluster_trend_proc',
    },
    {
        value: 'host_usage',
        label: 'Host Usage',
        procName: 'virtual_hosts_in_cluster_usage_history_proc',
    }
];

export default function ClusterTrends({title}) {
    const { theme, client, periodId } = useVsiAppContext();
    const [searchParams, setSearchParams] = useSearchParams();
    useTitleFromComponent(`${encodeURI(searchParams.get('cluster_name')) || 'N/A'} - ${title}`);

    const [chartType, setChartType] = useState(chartTypes.find(type => type.value === searchParams.get('chart_type')) || chartTypes[0]);

    const [{recordset: trendData}, {isLoading: isTrendDataLoading}] = useDataWithRoute(`/api/v6/report/${chartType.procName}?client_id=${client.clientId}&period_id=${periodId}&vcenter_name=${encodeURI(searchParams.get('vcenter_name'))}&cluster_name=${encodeURI(searchParams.get('cluster_name'))}`);

    const [filteredData, setFilteredData] = useState([]);
    const [formattedChartData, setFormattedChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState({});
    const [gridData, setGridData] = useState([]);

    const [axis1Type, setAxis1Type] = useState(metricTypes.find(type => type.value === searchParams.get('axis_1')) || metricTypes[0]);
    const [axis2Type, setAxis2Type] = useState(metricTypes.find(type => type.value === searchParams.get('axis_2')) || metricTypes[1]);

    const [usageMetricType, setUsageMetricType] = useState(usageMetricTypes.find(type => type.value === searchParams.get('metric_type')) || usageMetricTypes[0]);
    const [usageMeasurementType, setUsageMeasurementType] = useState(usageMeasurementTypes.find(type => type.value === searchParams.get('measurement')) || usageMeasurementTypes[0]);

    const defineChartConfigs = () => {
        return [...axis1Type.chartDefs.map(def => ({...def, yAxis: 0,})), ...axis2Type.chartDefs.map(def => ({
            ...def,
            yAxis: 1
        }))];
    }

    useEffect(() => {
        setFilteredData(trendData);
        setGridData([...trendData || []].reverse());
    }, [trendData]);

    useEffect(() => {
        if (chartType.value === "history") {
            searchParams.delete('metric_type');
            searchParams.delete('measurement');

            const formattedChart = formatDualAxisStockChartConfig(filteredData || [], defineChartConfigs());
            setFormattedChartData(formattedChart.series);
            setChartConfig({
                yAxis: [
                    {
                        title: {
                            text: axis1Type.label,
                        }
                    },
                    {
                        title: {
                            text: axis2Type.label,
                        },
                        opposite: true
                    }
                ],
            });
            setSearchParams(searchParams);
        } else if (chartType.value === "host_usage") {
            searchParams.delete('axis_1');
            searchParams.delete('axis_2');

            const foundHosts = {};
            const findHosts = filteredData?.map(datum => {
                if (!foundHosts[datum.esx_host_name]) {
                    foundHosts[datum.esx_host_name] = true;
                }

                const returnDatum = {}

                for (let dtKey in datum) {
                    // check if in the key (no prototypes) and if it's capitalized
                    if (datum.hasOwnProperty(dtKey)) {
                        // lowercasing...
                        returnDatum[!dtKey.charAt(0).match(/[A-Z\u00C0-\u00DC]+/) ? dtKey : dtKey.toLowerCase()] = datum[dtKey]
                    }
                }

                return returnDatum;
            });

            const fieldHosts = Object.keys(foundHosts).map(dc => ({
                name: dc,
                field: usageMeasurementType[usageMetricType.value],
            }));

            const formattedUsageChart = formatStockChartConfig(findHosts?.sort((a, b) => new Date(a) - new Date(b)) || [], fieldHosts, 'esx_host_name');

            setFormattedChartData(formattedUsageChart.series);
            setChartConfig({
                yAxis: {
                    title: {
                        text: usageMetricType[usageMeasurementType.value],
                    },
                }
            })
            setSearchParams(searchParams);
        }
    }, [filteredData, axis1Type, axis2Type, chartType, usageMetricType, usageMeasurementType]);

    const onAxis1Change = (axis) => {
        setAxis1Type(axis);
        searchParams.set('axis_1', axis.value);
        setSearchParams(searchParams, {replace: true});
    };

    const onAxis2Change = (axis) => {
        setAxis2Type(axis);
        searchParams.set('axis_2', axis.value);
        setSearchParams(searchParams, {replace: true});
    };

    const onChartTypeChange = type => {
        setChartType(type);
        searchParams.set('chart_type', type.value);
        setSearchParams(searchParams, {replace: true});
    }

    const onMetricTypeChange = type => {
        setUsageMetricType(type);
        searchParams.set('metric_type', type.value);
        setSearchParams(searchParams, {replace: true});
    }

    const onMeasurementTypeChange = type => {
        setUsageMeasurementType(type);
        searchParams.set('measurement', type.value);
        setSearchParams(searchParams, {replace: true});
    }

    const filterChanged = (eventData) => {
        let filteredData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            filteredData.push(node.data);
        });

        setFilteredData(filteredData);
    };

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>Cluster Trends - {searchParams.get('cluster_name') || 'N/A'}</VsiH4>
            <VsiBubble noAutobot>
                <div className={"w-100 d-flex"}>
                    <VsiLabeledSelect options={chartTypes}
                                      isLoading={isTrendDataLoading}
                                      isDisabled={isTrendDataLoading}
                                      label={'Chart Type'}
                                      minWidth={200}
                                      maxWidth={225}
                                      onChange={onChartTypeChange}
                                      defaultValue={chartType}/>
                    {chartType.value === "history" ? (
                        <>
                            <VsiLabeledSelect options={metricTypes.filter(({value}) => value !== axis2Type.value)}
                                              isLoading={isTrendDataLoading}
                                              isDisabled={isTrendDataLoading}
                                              label={'Left Axis'}
                                              minWidth={200}
                                              maxWidth={225}
                                              onChange={onAxis1Change}
                                              defaultValue={axis1Type}/>
                            <VsiLabeledSelect options={metricTypes.filter(({value}) => value !== axis1Type.value)}
                                              isLoading={isTrendDataLoading}
                                              isDisabled={isTrendDataLoading}
                                              label={'Right Axis'}
                                              minWidth={200}
                                              maxWidth={225}
                                              onChange={onAxis2Change}
                                              defaultValue={axis2Type}/>
                        </>
                    ) : null}
                    {chartType.value === "host_usage" ? (
                        <>
                            <VsiLabeledSelect options={usageMetricTypes}
                                              isLoading={isTrendDataLoading}
                                              isDisabled={isTrendDataLoading}
                                              label={'Metric Type'}
                                              minWidth={200}
                                              maxWidth={225}
                                              onChange={onMetricTypeChange}
                                              defaultValue={usageMetricType}/>
                            <VsiLabeledSelect options={usageMeasurementTypes}
                                              isLoading={isTrendDataLoading}
                                              isDisabled={isTrendDataLoading}
                                              label={'Measurement'}
                                              minWidth={200}
                                              maxWidth={225}
                                              onChange={onMeasurementTypeChange}
                                              defaultValue={usageMeasurementType}/>
                        </>
                    ) : null}
                </div>
            </VsiBubble>
            <VsiBubble unit={chartType.value} autobotInfo={{ yAxes: [axis1Type, axis2Type], secondaryMetric: [usageMetricType, usageMeasurementType] }}>
                <Stockchart series={formattedChartData}
                            config={chartConfig}
                            saveParams={{
                                clusterName: searchParams.get('cluster_name'),
                                vcenterName: searchParams.get('vcenter_name')
                                }}
                            />
            </VsiBubble>
            <VsiBubble unit={chartType.value}>
                <Grid data={gridData}
                      saveParams={{
                        chart_type: chartType.value,
                      }}
                      filterChanged={filterChanged}
                      columnDefs={chartType.value === "history" ? clusterModeling : clusterHostUsageHistory}/>
            </VsiBubble>
        </Card>
    )
}
